/**
 * LatestReflectionCard — subtle dashboard card showing the most recent
 * shared journal entry. Hides if no entries exist.
 */
import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import { BookOpen } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LatestReflectionCard() {
  const { household } = useAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!household?.household_id) return;
    const fetchLatest = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/households/${household.household_id}/journal/latest`,
          { credentials: "include" }
        );
        if (res.ok) {
          const data = await res.json();
          setEntry(data.entry);
        }
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    fetchLatest();
  }, [household?.household_id]);

  if (loading || !entry) return null;

  const date = entry.created_at
    ? new Date(entry.created_at).toLocaleDateString("en-NZ", {
        month: "short",
        day: "numeric",
      })
    : "";

  const typeLabel =
    entry.type === "annual_intention"
      ? "Intention"
      : entry.type === "cycle"
      ? "Cycle note"
      : "Reflection";

  return (
    <div
      className="bg-card border border-border rounded-xl p-4"
      data-testid="latest-reflection-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-4 h-4 text-[#1a4d3e]" />
        <span className="text-xs font-medium text-muted-foreground">
          Latest reflection
        </span>
      </div>
      <p className="text-sm leading-relaxed text-foreground" data-testid="latest-reflection-content">
        "{entry.content}"
      </p>
      <div className="flex items-center gap-2 mt-2">
        {entry.author_name && (
          <span className="text-[11px] text-muted-foreground">
            {entry.author_name}
          </span>
        )}
        {date && (
          <span className="text-[11px] text-muted-foreground/60">
            {typeLabel} · {date}
          </span>
        )}
      </div>
    </div>
  );
}
